import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

type Lang = 'fr' | 'en';

/**
 * A guest reaching this page came from a restaurant's QR landing page, which
 * has no account and no relation to whoever else is signed in on this
 * device, so, like landingStrings.ts, this is deliberately its own small
 * dictionary rather than routed through the app's i18next instance.
 */
const content: Record<Lang, {
  title: string;
  intro: string;
  bulletLocation: string;
  bulletVoluntary: string;
  bulletOptInPre: string;
  bulletOptInLink: string;
  bulletOptInLinkUrl: string;
  bulletOptInPost: string;
  bulletShare: string;
  bulletSecure: string;
  bulletContact: string;
  googleHeading: string;
  googleBullet1: string;
  googleBullet2: string;
  googleBullet3: string;
  googleBullet4Pre: string;
  googleBullet4Link: string;
  googleBullet4Post: string;
  googleBullet5Pre: string;
  googleBullet5Link: string;
  googleBullet5Post: string;
  footer: string;
}> = {
  fr: {
    title: 'Politique de confidentialité',
    intro: " s'engage à protéger votre vie privée. Cette page explique comment nous recueillons, utilisons et protégeons vos renseignements lorsque vous utilisez nos services de code QR et d'avis.",
    bulletLocation: "Lorsque vous scannez un code QR, nous enregistrons un emplacement approximatif (ville, région, pays et les premiers caractères d'un code postal) afin que le restaurant puisse voir quelles régions une campagne a rejointes. Nous ne conservons ni votre adresse IP ni aucun emplacement précis.",
    bulletVoluntary: "Mis à part cela, nous ne recueillons pas de renseignements personnels auprès des clients qui scannent un code QR, à moins que vous ne les fournissiez volontairement (par exemple via un formulaire d'inscription).",
    bulletOptInPre: "Si vous acceptez, sur la page d'accueil du code QR d'un restaurant, de recevoir des mises à jour ou des promotions, ces renseignements (votre courriel et/ou votre numéro de téléphone) sont recueillis et utilisés directement par ce restaurant, et non par ReviewCounter. Vous n'êtes jamais ajouté à une liste de diffusion sans avoir donné votre consentement explicite et volontaire. Le faire sans consentement contreviendrait à la",
    bulletOptInLink: 'Loi canadienne anti-pourriel (LCAP)',
    bulletOptInLinkUrl: 'https://ised-isde.canada.ca/site/loi-canadienne-anti-pourriel/fr',
    bulletOptInPost: ". Vous pouvez demander à être retiré de la liste de diffusion d'un restaurant en tout temps, en communiquant directement avec ce restaurant.",
    bulletShare: "ReviewCounter ne partage pas vos données avec des tiers, sauf si cela est nécessaire pour fournir nos services ou si la loi l'exige.",
    bulletSecure: "Toutes les données d'avis et d'analyse sont stockées de façon sécurisée et ne sont accessibles qu'aux administrateurs autorisés du restaurant.",
    bulletContact: "Pour toute question ou préoccupation, veuillez communiquer avec votre restaurant ou le soutien de ReviewCounter.",
    googleHeading: 'Données de compte Google',
    googleBullet1: "Un administrateur de restaurant peut choisir de connecter son profil d'établissement Google à ReviewCounter. Cela nécessite de se connecter avec son propre compte Google et d'accorder à ReviewCounter la permission d'accéder aux renseignements de ce profil d'établissement précis (comme son nom, son adresse, sa catégorie et ses avis).",
    googleBullet2: "Nous ne voyons ni ne stockons jamais le mot de passe du compte Google de l'administrateur. Google gère l'authentification directement; ReviewCounter reçoit uniquement un jeton d'accès à portée limitée pour les données d'entreprise que l'administrateur a explicitement autorisées.",
    googleBullet3: "Les données du profil d'établissement Google obtenues ainsi servent uniquement à afficher et à aider à gérer les avis et les renseignements d'entreprise de ce restaurant dans ReviewCounter. Elles ne sont ni vendues, ni partagées avec des tiers, ni utilisées à des fins publicitaires, et ne servent pas à entraîner des modèles d'IA génériques.",
    googleBullet4Pre: "Un administrateur peut révoquer l'accès de ReviewCounter à son compte Google en tout temps depuis ses",
    googleBullet4Link: 'paramètres de compte Google',
    googleBullet4Post: ', ou en communiquant avec le soutien de ReviewCounter.',
    googleBullet5Pre: "L'utilisation et le transfert par ReviewCounter des renseignements reçus des API Google respectent la",
    googleBullet5Link: "politique d'utilisation des données utilisateur des services API Google",
    googleBullet5Post: ", y compris les exigences d'utilisation limitée.",
    footer: 'Cette politique de confidentialité peut être mise à jour de temps à autre. Dernière mise à jour : septembre 2026.',
  },
  en: {
    title: 'Privacy Policy',
    intro: ' is committed to protecting your privacy. This page explains how we collect, use, and safeguard your information when you use our QR code and review services.',
    bulletLocation: 'When you scan a QR code, we record an approximate location (city, region, country, and the first few characters of a postal code) so the restaurant can see which areas a campaign reached. We do not store your IP address or any precise location.',
    bulletVoluntary: 'Beyond that, we do not collect personal information from customers scanning QR codes, unless you voluntarily provide it (e.g., via opt-in forms).',
    bulletOptInPre: "If you opt in on a restaurant's QR code landing page to receive updates or promotions, that information (your email and/or phone number) is collected and used directly by that restaurant, not by ReviewCounter. You are never added to a mailing list without giving your own explicit, voluntary consent. Doing so without consent would violate",
    bulletOptInLink: "Canada's Anti-Spam Legislation (CASL)",
    bulletOptInLinkUrl: 'https://ised-isde.canada.ca/site/canada-anti-spam-legislation/en',
    bulletOptInPost: ". You can ask to be removed from a restaurant's mailing list at any time by contacting that restaurant directly.",
    bulletShare: 'ReviewCounter does not share your data with third parties except as required to provide our services or by law.',
    bulletSecure: 'All review and analytics data is stored securely and only accessible to authorized restaurant admins.',
    bulletContact: 'For questions or concerns, please contact your restaurant or ReviewCounter support.',
    googleHeading: 'Google Account Data',
    googleBullet1: "A restaurant administrator may choose to connect their Google Business Profile to ReviewCounter. This requires signing in with their own Google account and granting ReviewCounter permission to access that specific business's Business Profile information (such as its name, address, category, and reviews).",
    googleBullet2: "We never see or store the administrator's Google account password. Google handles authentication directly; ReviewCounter only receives a limited-scope access token for the business data the administrator explicitly authorized.",
    googleBullet3: "Google Business Profile data obtained this way is used solely to display and help manage that restaurant's own reviews and business information within ReviewCounter. It is not sold, shared with third parties, or used to serve advertising, and it is not used to train generalized AI/ML models.",
    googleBullet4Pre: "An administrator can revoke ReviewCounter's access to their Google account at any time from their",
    googleBullet4Link: 'Google Account permissions',
    googleBullet4Post: ' page, or by contacting ReviewCounter support.',
    googleBullet5Pre: "ReviewCounter's use and transfer of information received from Google APIs adheres to the",
    googleBullet5Link: 'Google API Services User Data Policy',
    googleBullet5Post: ', including the Limited Use requirements.',
    footer: 'This privacy policy may be updated from time to time. Last updated: September 2026.',
  },
};

const Privacy: React.FC = () => {
  const [searchParams] = useSearchParams();
  // A guest arrives here from the landing page they were just reading, and
  // ?lang carries which one, so this page opens already matching. French
  // remains the fallback for a direct visit with no hint either way.
  const [lang, setLang] = useState<Lang>(searchParams.get('lang') === 'en' ? 'en' : 'fr');
  const c = content[lang];

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col items-center justify-center p-6">
      <div className="bg-white rounded-lg shadow p-6 max-w-2xl w-full flex flex-col items-center">
        <div className="self-end flex gap-1 mb-2">
          {(['fr', 'en'] as const).map((l) => (
            <button
              key={l}
              type="button"
              onClick={() => setLang(l)}
              className={`px-2 py-0.5 text-xs rounded border ${lang === l ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-600 border-gray-300'}`}
            >
              {l.toUpperCase()}
            </button>
          ))}
        </div>
        <img
          src="/reviewcounter-logo.png"
          alt="ReviewCounter"
          className="w-28 h-28 object-contain mb-4"
        />
        <h1 className="text-2xl font-bold text-teal-700 mb-4">{c.title}</h1>
        <p className="text-gray-700 mb-4 text-center">
          <strong>ReviewCounter</strong>{c.intro}
        </p>
        <ul className="text-gray-600 text-sm mb-4 list-disc pl-6">
          <li>{c.bulletLocation}</li>
          <li>{c.bulletVoluntary}</li>
          <li>
            {c.bulletOptInPre}{' '}
            <a
              href={c.bulletOptInLinkUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-teal-600"
            >
              {c.bulletOptInLink}
            </a>
            {c.bulletOptInPost}
          </li>
          <li>{c.bulletShare}</li>
          <li>{c.bulletSecure}</li>
          <li>{c.bulletContact}</li>
        </ul>

        <h2 className="text-lg font-semibold text-teal-700 mb-2 self-start">{c.googleHeading}</h2>
        <ul className="text-gray-600 text-sm mb-4 list-disc pl-6">
          <li>{c.googleBullet1}</li>
          <li>{c.googleBullet2}</li>
          <li>{c.googleBullet3}</li>
          <li>
            {c.googleBullet4Pre}{' '}
            <a
              href="https://myaccount.google.com/permissions"
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-teal-600"
            >
              {c.googleBullet4Link}
            </a>
            {c.googleBullet4Post}
          </li>
          <li>
            {c.googleBullet5Pre}{' '}
            <a
              href="https://developers.google.com/terms/api-services-user-data-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-teal-600"
            >
              {c.googleBullet5Link}
            </a>
            {c.googleBullet5Post}
          </li>
        </ul>

        <p className="text-gray-500 text-xs text-center">
          {c.footer}
        </p>
      </div>
    </div>
  );
};

export default Privacy;
